import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '@card-software/nexus-utilities';
import { ExchangeRate, ExchangeRateHistoryCommand, ExchangeRateSearchCommand } from '../models/exchange-rate/exchange-rate';

/** Local calendar date, no timezone shift. `toISOString()` is UTC, which moves a Honduran evening to the
 *  next day and would ask for the wrong day's rate. */
function formatDateOnly(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class ExchangeRateProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/exchange-rate';

  public getByCurrency(currency: number): Observable<ApiResponse<ExchangeRate>> {
    return this.httpClient.get<ApiResponse<ExchangeRate>>(`${this.baseUrl}/current/${currency}`);
  }
  /**
   * The rate in force on `date` — the newest one published on or before it, which is exactly what the API
   * books a document at (`CurrencyConverter.GetRateToUsdAsync`).
   *
   * Neither neighbour answers this. `getByCurrency` is today's rate whatever the document is dated, and
   * `searchExchangeRate` matches one exact day, so it comes back empty over a weekend or a holiday where a
   * posting happily uses Friday's. Previewing a document's rate with either one can promise a number the
   * posting will not use.
   */
  public getRateInForce(currency: number, date: Date): Observable<ApiResponse<ExchangeRate>> {
    const params = new HttpParams().set('date', formatDateOnly(date));
    return this.httpClient.get<ApiResponse<ExchangeRate>>(`${this.baseUrl}/in-force/${currency}`, { params });
  }

  public searchExchangeRate(command: ExchangeRateSearchCommand): Observable<ApiResponse<ExchangeRate[]>> {
    return this.httpClient.post<ApiResponse<ExchangeRate[]>>(`${this.baseUrl}/search`, command);
  }
  /**
   * A page of the whole rate history, optionally bounded by a start/end date range (#5225).
   * Unlike `searchExchangeRate`, an empty command means every rate rather than today's.
   */
  public getHistory(
    command: ExchangeRateHistoryCommand,
    pageNumber = 1,
    pageSize = 25,
  ): Observable<ApiResponse<PagedResult<ExchangeRate>>> {
    const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
    return this.httpClient.post<ApiResponse<PagedResult<ExchangeRate>>>(`${this.baseUrl}/history`, command, { params });
  }

  public createExchangeRate(exchangeRate: ExchangeRate): Observable<ApiResponse<ExchangeRate>> {
    return this.httpClient.post<ApiResponse<ExchangeRate>>(`${this.baseUrl}`, exchangeRate);
  }
  public updateExchangeRate(id: string, exchangeRate: ExchangeRate): Observable<ApiResponse<ExchangeRate>> {
    return this.httpClient.put<ApiResponse<ExchangeRate>>(`${this.baseUrl}/${id}`, exchangeRate);
  }
}
